import { StudioProjectLifecycleViewV1Schema, } from "../contracts/studio-project-lifecycle.js";
export const SIMPLE_PROJECT_PHASES = [
    { id: "waiting_for_client", label: "Waiting for client" },
    { id: "review_and_build", label: "Review & build" },
    { id: "generating", label: "Generating" },
    { id: "preview_live", label: "Preview live" },
    { id: "client_approved", label: "Client approved" },
    { id: "packaging", label: "Packaging" },
    { id: "handoff_ready", label: "Handoff ready" },
];
export function latestRun(runs) {
    if (runs.length === 0)
        return null;
    return [...runs].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] ?? null;
}
const UI_ACTIVE_GENERATION_STATUSES = [
    "PENDING",
    "GENERATING",
    "GENERATED",
    "QA_QUEUED",
    "QA_RUNNING",
];
/** Active generation with no progress for this long is treated as stuck (worker/queue/DB). */
export const STALE_ACTIVE_GENERATION_MS = 3 * 60 * 1000;
export function isStaleActiveGenerationRun(run, nowMs = Date.now()) {
    if (!run)
        return false;
    if (!UI_ACTIVE_GENERATION_STATUSES.includes(run.status))
        return false;
    const updatedAtMs = Date.parse(run.updatedAt ?? run.createdAt);
    if (Number.isNaN(updatedAtMs))
        return false;
    return nowMs - updatedAtMs > STALE_ACTIVE_GENERATION_MS;
}
export function isGeneratingRun(runs) {
    const run = latestRun(runs);
    if (!run)
        return false;
    if (isStaleActiveGenerationRun(run))
        return false;
    return UI_ACTIVE_GENERATION_STATUSES.includes(run.status);
}
const PREVIEW_READY_RUN_STATUSES = [
    "QA_PASSED",
    "QA_PASSED_WITH_WARNINGS",
    "EXPORT_READY",
    "EXPORTING",
    "EXPORTED",
];
function isPreviewReadyRun(status) {
    if (!status)
        return false;
    return PREVIEW_READY_RUN_STATUSES.includes(status) || status === "SUPERSEDED";
}
/**
 * True when a generation run status is eligible for agency/client preview
 * (QA pass, warn-pass, or export path). Does not include SUPERSEDED.
 */
export function isPreviewEligibleRunStatus(status) {
    if (!status)
        return false;
    return PREVIEW_READY_RUN_STATUSES.includes(status);
}
/** True when API should load departure-kit status for lifecycle (post-QA / export path). */
export function runStatusWarrantsDepartureKitLoad(status) {
    return isPreviewEligibleRunStatus(status);
}
/** Preview is unlocked when review metadata, client link, or latest run says so. */
export function resolveStudioHasPreview(input) {
    if (input.clientReview?.latestPreview)
        return true;
    if (input.clientReview?.clientLink?.status === "PREVIEW_READY")
        return true;
    const latest = latestRun(input.runs ?? []);
    return isPreviewReadyRun(latest?.status);
}
export function isPackagingDepartureKit(departureKit, _latestRunStatus) {
    if (!departureKit)
        return false;
    const status = departureKit.issuanceStatus;
    if (status === "EXPORT_QUEUED" || status === "EXPORTING" || status === "COMPOSING") {
        return true;
    }
    // Legacy persisted REQUESTED + EXPORT_READY run (pre-split records)
    if (status === "REQUESTED" && _latestRunStatus === "EXPORT_READY") {
        return true;
    }
    return false;
}
/** Human label for departure kit issuance in Studio handoff panels. */
export function humanDepartureKitIssuanceLabel(issuanceStatus) {
    switch (issuanceStatus) {
        case "READY":
            return "Ready to download";
        case "ELIGIBLE":
            return "Ready to package";
        case "EXPORT_QUEUED":
        case "EXPORTING":
        case "COMPOSING":
            return "Packaging in progress";
        case "FAILED":
            return "Export failed";
        case "REQUESTED":
            return "Ready to package";
        default:
            return "Not available yet";
    }
}
export function isHandoffReadyDepartureKit(departureKit) {
    if (!departureKit)
        return false;
    return departureKit.downloadAvailable || departureKit.issuanceStatus === "READY";
}
/** True while export/DK composition is in flight after client approval (poll until this clears). */
export function isAwaitingHandoffPackaging(input) {
    if (!input.hasApproval || isHandoffReadyDepartureKit(input.departureKit)) {
        return false;
    }
    const latest = latestRun(input.runs);
    return isPackagingDepartureKit(input.departureKit, latest?.status);
}
export function resolveExportRunId(input) {
    if (input.departureKit?.runId)
        return input.departureKit.runId;
    const passing = input.runs.filter((run) => ["QA_PASSED", "QA_PASSED_WITH_WARNINGS", "EXPORTED", "EXPORTING", "EXPORT_READY"].includes(run.status));
    if (passing.length === 0)
        return null;
    return [...passing].sort((a, b) => (b.updatedAt ?? b.createdAt).localeCompare(a.updatedAt ?? a.createdAt))[0]?.runId ?? null;
}
export function deriveSimpleProjectFacts(input) {
    const graph = input.activeGraph?.businessGraph;
    if (graph) {
        const contact = [graph.business.primaryPhone, graph.business.primaryEmail].filter(Boolean).join(" · ");
        return {
            businessName: graph.business.businessName,
            contact: contact.length > 0 ? contact : "—",
            services: graph.services.map((service) => service.name),
            serviceAreas: graph.operations.serviceAreas.map((area) => area.name),
            description: graph.business.description?.trim() || "—",
        };
    }
    const intake = input.intakeSummary;
    if (intake) {
        const contact = [intake.primaryPhone, intake.primaryEmail].filter(Boolean).join(" · ");
        return {
            businessName: intake.businessName,
            contact: contact.length > 0 ? contact : "—",
            services: (intake.services ?? []).map((service) => service.name),
            serviceAreas: (intake.serviceAreas ?? []).map((area) => area.name),
            description: intake.description?.trim() || "—",
        };
    }
    if (input.businessName) {
        return {
            businessName: input.businessName,
            contact: "—",
            services: [],
            serviceAreas: [],
            description: "—",
        };
    }
    return null;
}
const QA_FAILURE_RUN_STATUSES = ["QA_FAILED", "FAILED", "CANCELLED"];
export function isQaFailureRunStatus(status) {
    if (!status)
        return false;
    return QA_FAILURE_RUN_STATUSES.includes(status);
}
export function isActiveGenerationRunStatus(status) {
    if (!status)
        return false;
    return UI_ACTIVE_GENERATION_STATUSES.includes(status);
}
export function deriveSimpleProjectPhase(input) {
    const latest = latestRun(input.runs);
    const staleGeneration = isStaleActiveGenerationRun(latest);
    const generating = isGeneratingRun(input.runs);
    const previewReady = input.hasPreview && !generating && (latest == null || isPreviewReadyRun(latest.status));
    const hasApproval = Boolean(input.clientReview?.latestApproval);
    const handoffReady = isHandoffReadyDepartureKit(input.departureKit);
    const qaFailure = isQaFailureRunStatus(latest?.status) || staleGeneration;
    const packaging = hasApproval && isPackagingDepartureKit(input.departureKit, latest?.status);
    const showPreviewPanel = previewReady;
    const staleApproval = hasApproval &&
        input.clientReview != null &&
        !input.clientReview.approvalMatchesLatestPassingArtifact;
    const exportRunId = resolveExportRunId({ runs: input.runs, departureKit: input.departureKit });
    let phase;
    if (generating) {
        phase = "generating";
    }
    else if (handoffReady) {
        phase = "handoff_ready";
    }
    else if (packaging) {
        phase = "packaging";
    }
    else if (hasApproval && previewReady) {
        phase = "client_approved";
    }
    else if (previewReady) {
        phase = "preview_live";
    }
    else if (qaFailure) {
        phase = "review_and_build";
    }
    else if (input.intakeSubmitted || input.hasActiveRevision) {
        phase = "review_and_build";
    }
    else if (input.intakeSummary) {
        phase = "waiting_for_client";
    }
    else {
        phase = "waiting_for_client";
    }
    const order = SIMPLE_PROJECT_PHASES.map((step) => step.id);
    const currentIndex = order.indexOf(phase);
    const phaseStatus = Object.fromEntries(order.map((id, index) => {
        if (index < currentIndex)
            return [id, "done"];
        if (index === currentIndex)
            return [id, "current"];
        return [id, "upcoming"];
    }));
    const primary = qaFailure
        ? {
            label: "Retry build",
            actionId: "confirm-build",
            title: staleGeneration ? "Generation timed out" : "Generation needs attention",
            copy: staleGeneration
                ? "The build did not finish in time. Run pnpm db:migrate and pnpm dev:worker, then retry."
                : "The latest site run did not pass quality gate. Review facts and try building again.",
        }
        : primaryForPhase(phase);
    return {
        phase,
        phaseLabel: SIMPLE_PROJECT_PHASES.find((step) => step.id === phase)?.label ?? phase,
        phaseStatus,
        primaryCta: primary.label,
        primaryActionId: primary.actionId,
        nowTitle: primary.title,
        nowCopy: primary.copy,
        showFactsPanel: phase === "review_and_build",
        showPreviewPanel,
        showSecondaryPortal: !["preview_live", "handoff_ready"].includes(phase),
        showHandoffPanel: hasApproval || handoffReady || packaging,
        showStaleApprovalBanner: staleApproval,
        staleApprovalSummary: staleApproval
            ? (input.departureKit?.passingVsApproved?.clientSafeSummary ??
                "Client approved an older preview — share the latest preview before packaging handoff.")
            : null,
        facts: null,
        primaryDisabled: phase === "generating" || phase === "packaging",
        exportRunId,
        qaFailure,
    };
}
function primaryForPhase(phase) {
    switch (phase) {
        case "waiting_for_client":
            return {
                label: "Copy client link",
                actionId: "client-link",
                title: "Waiting for client",
                copy: "Share the client portal link so your client can submit intake.",
            };
        case "review_and_build":
            return {
                label: "Confirm facts & build preview",
                actionId: "confirm-build",
                title: "Review facts before building",
                copy: "Confirm the business facts below, then Enrein will validate the profile and generate a preview.",
            };
        case "generating":
            return {
                label: "Generating website…",
                actionId: "none",
                title: "Building preview",
                copy: "The site generator is running. Ensure the worker is up (apps/worker) — preview usually appears within a minute.",
            };
        case "preview_live":
            return {
                label: "Copy preview link",
                actionId: "copy-preview",
                title: "Preview is ready",
                copy: "Share the preview link with your client. They approve in the client portal — then you package handoff here.",
            };
        case "client_approved":
            return {
                label: "Package handoff",
                actionId: "package-handoff",
                title: "Client approved",
                copy: "Your client approved the preview. Package the Departure Kit to deliver ownership-grade handoff files.",
            };
        case "packaging":
            return {
                label: "Packaging handoff…",
                actionId: "none",
                title: "Packaging handoff",
                copy: "Export and Departure Kit composition are running. Download will unlock when the bundle is ready.",
            };
        case "handoff_ready":
            return {
                label: "Download Departure Kit",
                actionId: "download-handoff",
                title: "Handoff ready",
                copy: "The Departure Kit bundle is ready. Download here — your client can download from their portal Handoff section.",
            };
        default:
            return {
                label: "Continue",
                actionId: "client-link",
                title: "Continue this project",
                copy: "Take the next proof-backed step.",
            };
    }
}
const DEFAULT_IDLE_POLL_MS = 10_000;
const ACTIVE_POLL_MS = 2_000;
export function deriveLifecyclePollHints(input) {
    const reasons = [];
    if (input.flags.generating)
        reasons.push("generation_active");
    if (input.flags.packaging)
        reasons.push("export_active");
    if (input.flags.staleGeneration)
        reasons.push("stale_generation");
    if (input.flags.generationLock)
        reasons.push("generation_lock");
    const active = input.flags.generating || input.flags.packaging;
    return {
        suggestedIntervalMs: active ? ACTIVE_POLL_MS : DEFAULT_IDLE_POLL_MS,
        reasons,
    };
}
export function deriveStudioProjectLifecycle(input) {
    const hasPreview = resolveStudioHasPreview({
        clientReview: input.clientReview,
        runs: input.runs,
    });
    const phaseState = deriveSimpleProjectPhase({
        intakeSummary: input.intakeSummary ?? null,
        hasActiveRevision: input.hasActiveRevision,
        intakeSubmitted: input.intakeSubmitted,
        hasPreview,
        runs: input.runs,
        clientReview: input.clientReview,
        departureKit: input.departureKit,
    });
    const latest = latestRun(input.runs);
    const staleGeneration = isStaleActiveGenerationRun(latest);
    const generating = phaseState.phase === "generating";
    const packaging = phaseState.phase === "packaging";
    const handoffReady = phaseState.phase === "handoff_ready";
    const flags = {
        generating,
        packaging,
        handoffReady,
        qaFailure: phaseState.qaFailure,
        staleGeneration,
        generationLock: Boolean(input.generationLock?.locked),
        hasPreview,
        hasApproval: Boolean(input.clientReview?.latestApproval),
    };
    const pollHints = deriveLifecyclePollHints({ flags });
    return StudioProjectLifecycleViewV1Schema.parse({
        schemaId: "studio.projectLifecycleView.v1",
        agencyId: input.agencyId,
        projectId: input.projectId,
        generatedAt: input.generatedAt,
        simplePhase: phaseState.phase,
        phaseLabel: phaseState.phaseLabel,
        phaseStatus: phaseState.phaseStatus,
        primaryCta: phaseState.primaryCta,
        primaryActionId: phaseState.primaryActionId,
        primaryDisabled: phaseState.primaryDisabled,
        nowTitle: phaseState.nowTitle,
        nowCopy: phaseState.nowCopy,
        flags,
        exportRunId: phaseState.exportRunId,
        pollHints,
        latestRun: latest ?? undefined,
        clientReview: input.clientReview ?? undefined,
        departureKit: input.departureKit ?? undefined,
        intakeSummary: input.intakeSummary ?? undefined,
        generationLock: input.generationLock ?? undefined,
    });
}
//# sourceMappingURL=derive-studio-project-lifecycle.js.map